/* eslint-disable */
import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  Box,
  Card,
  CardContent,
  useTheme,
  colors,
  Typography
} from '@material-ui/core';
import { Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useSelector } from 'react-redux';
import { currentmonth } from 'feactures/section/sectionSlice';

Chart.register(ChartDataLabels);

const CHART_COLORS = [
  '#3B5790',
  '#435F9F',
  '#496DB5',
  '#5C80BE',
  '#718CC6',
  '#84A0CE',
  '#92A7D2',
  '#A4B6D9',
  '#B6C4E1',
  '#BBC9E4',
  '#C1CFE7'
];

/**
 * @param {object} props
 * @param {object} props.graphdata
 * @param {'around'|'legend'} [props.labelLayout]
 *   around: 円の外にラベル（カテゴリ名向け）
 *   legend: 円は大きめ＋右側に全文凡例（ASIN向け・切れ防止）
 */
const CategorySharegraph = (props) => {
  const { graphdata, labelLayout = 'around', ...cardProps } = props;
  const useSideLegend = labelLayout === 'legend';
  const yearmonth = useSelector(currentmonth);
  let monthlySales = 0;
  let calcTotal = 0;
  let tanni = '';
  if (graphdata.total >= 1000000000000) {
    calcTotal = graphdata.total / 1000000000000;
    monthlySales = calcTotal.toFixed(1);
    tanni = '兆円';
  } else if (graphdata.total >= 100000000) {
    monthlySales = Math.round(graphdata.total / 100000000);
    tanni = '億円';
  } else if (graphdata.total >= 10000) {
    monthlySales = Math.round(graphdata.total / 10000);
    tanni = '万円';
  } else if (graphdata.total < 10000) {
    monthlySales = 1;
    tanni = '万円';
  } else if ((graphdata.total = 0)) {
    monthlySales = 0;
    tanni = '円';
  }

  const theme = useTheme();
  const percentages = (graphdata.percentage || []).filter((num) => num > 0);
  const labels = (graphdata.label || []).slice(0, percentages.length);

  const data = {
    datasets: [
      {
        data: percentages,
        backgroundColor: CHART_COLORS,
        borderColor: colors.common.white,
        hoverBorderColor: colors.common.white
      }
    ],
    labels
  };

  const options = {
    cutoutPercentage: 90,
    layout: useSideLegend
      ? { padding: { top: 8, bottom: 8, left: 8, right: 8 } }
      : { padding: { top: 30, bottom: 30 } },
    legend: {
      display: false
    },
    maintainAspectRatio: false,
    responsive: true,
    height: '100%',
    tooltips: {
      enabled: false
    },
    plugins: {
      legend: {
        display: false,
        position: 'bottom'
      },
      tooltip: {
        enabled: true,
        filter: function (item) {
          // around モード: 上位5件は外側ラベル、それ以外は tooltip
          if (useSideLegend) return true;
          return item.dataIndex >= 5;
        },
        callbacks: {
          label: function (tooltipItem) {
            const raw = tooltipItem.raw;
            const label = labels[tooltipItem.dataIndex] || '';
            return `${label} ${raw}%`;
          }
        }
      },
      datalabels: {
        display: useSideLegend
          ? false
          : (context) => context.dataIndex < 5,
        align: 'end',
        anchor: 'end',
        clip: false,
        clamp: false,
        color: theme.palette.text.secondary,
        font: {
          family: "'M PLUS 1p', sans-serif"
        },
        formatter: (value, context) => {
          if (context.dataIndex >= 5) {
            return '';
          }
          return `${labels[context.dataIndex]} ${value}%`;
        }
      }
    }
  };

  return (
    <Card
      {...cardProps}
      sx={{
        padding: 0,
        overflow: 'visible',
        boxShadow: 'none',
        width: '100%',
        height: '100%',
        backgroundColor: 'transparent'
      }}
    >
      <CardContent
        sx={{
          padding: 0,
          overflow: 'visible',
          display: 'flex',
          flexDirection: useSideLegend ? 'row' : 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: useSideLegend ? 1 : 0,
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
          ':last-child': { pb: 0 }
        }}
      >
        <Box
          sx={{
            height: useSideLegend ? 280 : 300,
            width: useSideLegend ? 'auto' : '100%',
            flex: useSideLegend ? '1 1 58%' : undefined,
            minWidth: useSideLegend ? 200 : 0,
            position: 'relative',
            overflow: 'visible'
          }}
        >
          <Doughnut data={data} options={options} />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              pointerEvents: 'none'
            }}
          >
            <Typography fontSize="14px">
              {yearmonth.replace('-', '年')}月
            </Typography>
            <Box display="flex" alignItems="end" justifyContent="center">
              <Typography fontWeight="bold" color="#7288C5" fontSize="20px">
                {monthlySales.toLocaleString()}
              </Typography>
              <Typography fontSize="14px">{tanni}</Typography>
            </Box>
          </Box>
        </Box>

        {useSideLegend ? (
          <Box
            sx={{
              flex: '1 1 42%',
              minWidth: 0,
              maxHeight: 280,
              overflowY: 'auto',
              pl: 0.5,
              pr: 0.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75
            }}
          >
            {labels.map((label, index) => (
              <Box
                key={`${label}-${index}`}
                display="flex"
                alignItems="center"
                sx={{ minWidth: 0, gap: 1 }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 12,
                    height: 12,
                    borderRadius: '2px',
                    bgcolor: CHART_COLORS[index % CHART_COLORS.length]
                  }}
                />
                <Typography
                  fontSize="12px"
                  color={theme.palette.text.secondary}
                  sx={{
                    minWidth: 0,
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-all',
                    lineHeight: 1.3
                  }}
                >
                  {label} {percentages[index]}%
                </Typography>
              </Box>
            ))}
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default CategorySharegraph;
